/**
 * `/agents` — one page for the whole agent fleet.
 *
 * Every agent family gets a card answering three questions in order: is it
 * running, what has it been doing, and what is it told to do. Before this page
 * that meant `/status` for liveness, `/pr` `/inbox` `/rota` for the work,
 * `/preload` for one agent's prompt, `/config` for the chat agent's, and a
 * terminal on Mario's laptop for the on/off switch.
 *
 * THE HONESTY RULES, which are the reason several controls here are disabled or
 * absent rather than merely unstyled:
 *
 *  • A toggle appears only where a runner actually READS the flag. Everywhere
 *    else the card states what turns that lane off instead — `/preload` already
 *    learned that "an editor that promises an effect it cannot deliver is worse
 *    than no editor".
 *  • Resuming cannot START a lane whose launchd agent is unloaded, so for those
 *    the control is disabled with the launchctl label spelled out.
 *  • None of the four resting states is `degraded`. A board that is red about a
 *    decision is a board people stop reading — and one that renders four
 *    different situations in the same grey word is a board people stop
 *    believing. `stood_down` / `idle` / `not_yet_run` / `unobserved` are
 *    separate because they take separate actions; see AgentLaneStatus.
 *
 * Styling reuses the global vocabulary (`.home-card`, `.audit-table`,
 * `.stat-grid`, `.tag`) — see styles.css. Note `.tag-danger` and `.tag-info`
 * have NO global definition (only under `.page-studio-config`), so the status
 * pills map to `.tag-success` / `.tag-error` / `.tag-warn`, which exist.
 */

import { useCallback, useEffect, useState, type JSX } from "react";
import { Link } from "react-router-dom";

import {
  api,
  type AgentActivityResponse,
  type AgentActivityRow,
  type AgentFamilyRow,
  type AgentLaneRow,
  type AgentLaneStatus,
  type AgentsBoardResponse,
  type PreloadAgentCard,
  type PreloadResponse,
} from "../lib/api";
import { generateRequestIdV7 } from "../lib/dispatch";
import { useDispatch } from "../lib/use-dispatch";
import {
  useCachedResource,
  type ResourceSnapshot,
} from "../lib/resource-cache";
import {
  invalidateAgents,
  invalidateStudioConfig,
} from "../lib/resource-cache-invalidation";
import { studioFormat } from "../lib/studio-tz";
import { useCursorFeed } from "../lib/use-cursor-feed";
import { LoadMoreSentinel } from "../components/LoadMoreSentinel";
import { ConnectorAiRow } from "../components/ConnectorAiRow";
import {
  ConnectorChatRow,
  type ConnectorChatState,
} from "../components/ConnectorChatRow";
import { AgentPreloadEditor } from "../components/AgentPreloadEditor";
import {
  applyConnectorCapability,
  applyConnectorState,
  applyWebchatEnabled,
  type ConnectorAiState,
} from "../lib/studio-config-patch";
import {
  CONNECTOR_DESCRIPTORS,
  type AiAutonomyMode,
  type ConnectorCapability,
  type ConnectorKey,
} from "@layer/canonical-data/connectors";

const ACTIVITY_PAGE = 50;

/** Status → an existing global tag class. See the header on `.tag-danger`. */
const STATUS_TAG: Record<AgentLaneStatus, string> = {
  live: "tag tag-success",
  degraded: "tag tag-error",
  paused: "tag tag-warn",
  // The four resting states stay NEUTRAL. Giving `stood_down` a warn colour
  // was tempting and would be wrong: a fleet switched off in July is not a
  // developing situation, and colouring decisions amber is how a board teaches
  // people to stop looking at amber.
  stood_down: "tag",
  idle: "tag",
  not_yet_run: "tag",
  unobserved: "tag",
};

/**
 * The pill text. Each says what IS, and `STATUS_HINT` below says what to do —
 * the split `dormant` could not make, because one word cannot mean "load a
 * launchd job", "wait for the 1st" and "nothing is wrong" at once.
 */
const STATUS_LABEL: Record<AgentLaneStatus, string> = {
  live: "live",
  degraded: "needs attention",
  paused: "paused",
  stood_down: "stood down",
  idle: "idle",
  not_yet_run: "not yet run",
  unobserved: "unobserved",
};

/** Hover/`title` copy: what would make this lane run. Empty = it is running. */
const STATUS_HINT: Record<AgentLaneStatus, string> = {
  live: "",
  degraded: "Its last heartbeat failed or went stale unexpectedly.",
  paused: "Switched off through a control. Resume it here or clear the local PAUSED file.",
  stood_down:
    "Its runner is not loaded — no switch on this page will start it. Load the launchd job on the machine that hosts it.",
  idle: "Running, with nothing to do right now. Resting, not off.",
  not_yet_run:
    "Watched and installed, with no run on record yet. Nothing to do — its next scheduled run is the fix.",
  unobserved:
    "Nothing watches this lane, so the page can make no claim either way. Not a verdict about the lane.",
};

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  dateStyle: "medium",
  timeStyle: "short",
};

function when(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

/** `ok` / `error:<code>` / `ok:<detail>` split on the FIRST colon (Home's rule). */
function splitOutcome(outcome: string | null): { verdict: string; detail: string | null } {
  if (outcome === null) return { verdict: "—", detail: null };
  const idx = outcome.indexOf(":");
  if (idx === -1) return { verdict: outcome, detail: null };
  return { verdict: outcome.slice(0, idx), detail: outcome.slice(idx + 1) };
}

function outcomeClass(verdict: string): string {
  if (verdict === "ok") return "tag tag-success";
  if (verdict === "error") return "tag tag-error";
  return "tag";
}

// ---------------------------------------------------------------------------
// Activity feed.
// ---------------------------------------------------------------------------

interface ActivityFeed {
  rows: ReadonlyArray<AgentActivityRow>;
  busy: boolean;
  error: string | null;
  done: boolean;
  unavailable: string | null;
  loadMore: () => void;
}

/**
 * Keyset-paged audit feed for one lane or family — `useCursorFeed`, ONE
 * implementation with the Home audit card and the `/data` browser
 * (2026-09-08; until then a third hand copy of the de-duplicate-on-`id` and
 * cursor-must-advance guards lived here). The feed arms the first time the
 * family card opens and then stays loaded: a later collapse/expand neither
 * refetches nor blanks it. A lane that writes no audit rows answers
 * `unavailable_reason` with an empty page and no cursor — a reason, not an
 * error — which ends the feed like any last page; the panel reads the reason
 * off that page.
 */
function useAgentActivity(agent: string, open: boolean): ActivityFeed {
  const [armed, setArmed] = useState<boolean>(open);
  useEffect(() => {
    if (open) setArmed(true);
  }, [open]);
  const feed = useCursorFeed<AgentActivityResponse>({
    identity: agent,
    enabled: armed,
    fetchPage: (cursor) => api.agentsActivity(agent, cursor, ACTIVITY_PAGE),
    rowKey: (row) => row.id,
  });
  return {
    rows: feed.rows,
    busy: feed.loadingFirst || feed.loadingMore,
    error: feed.error,
    done: feed.first !== null && !feed.hasMore && !feed.loadingFirst,
    unavailable: feed.first?.unavailable_reason ?? null,
    loadMore: feed.loadMore,
  };
}

function ActivityPanel({ agent, open }: { agent: string; open: boolean }): JSX.Element {
  const feed = useAgentActivity(agent, open);

  if (feed.unavailable !== null) {
    return (
      <p className="empty">
        No activity feed — {feed.unavailable}
      </p>
    );
  }
  if (feed.error !== null) {
    return <p className="empty">Could not load activity: {feed.error}</p>;
  }
  if (feed.rows.length === 0 && feed.done) {
    return <p className="empty">Nothing dispatched in the last 30 days.</p>;
  }

  return (
    <>
      <table className="audit-table">
        <colgroup>
          <col className="audit-col-when" />
          <col className="audit-col-action" />
          <col className="audit-col-actor" />
          <col className="audit-col-outcome" />
        </colgroup>
        <thead>
          <tr>
            <th>When</th>
            <th>Action</th>
            <th>Actor</th>
            <th>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {feed.rows.map((r) => {
            const { verdict, detail } = splitOutcome(r.outcome);
            return (
              <tr key={r.id}>
                <td className="audit-cell-when" data-label="When">
                  {when(r.occurred_at)}
                </td>
                <td className="audit-cell-action" data-label="Action">
                  {r.action}
                </td>
                <td className="audit-cell-actor" data-label="Actor">
                  {r.actor}
                </td>
                <td data-label="Outcome">
                  <span className={outcomeClass(verdict)}>{verdict}</span>
                  {detail !== null && (
                    <span style={{ opacity: 0.7, marginLeft: 6 }}>{detail}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <LoadMoreSentinel onVisible={feed.loadMore} loading={feed.busy} />
      <p className="subtle home-audit-foot">
        {feed.rows.length} event{feed.rows.length === 1 ? "" : "s"} loaded · last 30 days
        {feed.done ? " · end of the log" : ""}
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Lane control.
// ---------------------------------------------------------------------------

function LaneToggle({
  lane,
  onSettled,
  disabled,
}: {
  lane: AgentLaneRow;
  onSettled: () => void;
  disabled: boolean;
}): JSX.Element | null {
  const d = useDispatch<Record<string, unknown>, { desired_state: string }>(
    "set_agent_desired_state",
  );
  const [optimistic, setOptimistic] = useState<"active" | "paused" | null>(null);
  const control = lane.control;
  if (control.kind !== "agent_control") return null;

  const shown = optimistic ?? control.desired_state;
  const paused = shown === "paused";
  // Resuming a lane whose launchd agent is unloaded changes nothing. Saying so
  // beats offering a button that saves green and has no effect.
  const inert = paused && !control.remote_start_effective;

  const flip = async (): Promise<void> => {
    const next = paused ? "active" : "paused";
    if (
      next === "paused" &&
      !window.confirm(
        `Pause ${control.agent_key}? This stands down its scheduled round AND its drain ` +
          `at the next tick. Approved work waits rather than executing; Telegram chat keeps working.`,
      )
    ) {
      return;
    }
    setOptimistic(next);
    const res = await d.run({ agent: control.agent_key, desired_state: next });
    if (!res.ok) {
      setOptimistic(null);
      return;
    }
    // Re-pin to the SERVER's value, not the one we sent (the Flows.tsx rule).
    setOptimistic(res.response.desired_state as "active" | "paused");
    onSettled();
  };

  return (
    <span className="agents-lane-control">
      <button
        type="button"
        onClick={() => void flip()}
        disabled={d.loading || disabled || inert}
        title={
          inert
            ? `No runner is ticking — load ${control.launchd_label} first.`
            : undefined
        }
      >
        {paused ? "Resume" : "Pause"}
      </button>
      {d.error !== null && (
        <span
          className="tag tag-error"
          style={{ marginLeft: 6 }}
          title={d.error.message}
        >
          {d.error.code}
        </span>
      )}
    </span>
  );
}

function ControlCell({
  lane,
  onSettled,
  controlsAvailable,
}: {
  lane: AgentLaneRow;
  onSettled: () => void;
  controlsAvailable: boolean;
}): JSX.Element {
  const c = lane.control;
  switch (c.kind) {
    case "agent_control":
      return (
        <>
          <LaneToggle lane={lane} onSettled={onSettled} disabled={!controlsAvailable} />
          {c.reason !== null && (
            <div className="subtle" style={{ fontSize: "0.82em" }}>
              {c.reason}
            </div>
          )}
          {!c.remote_start_effective && (
            <div className="subtle" style={{ fontSize: "0.82em" }}>
              No runner is ticking, so resuming here changes nothing — load{" "}
              <code>{c.launchd_label}</code> first.{" "}
              <Link to="/status" className="data-member-link">
                /status
              </Link>{" "}
              owns this verdict.
            </div>
          )}
        </>
      );
    case "connector":
      return (
        <span className="subtle">
          {c.enabled ? `on · ${c.autonomy ?? "enabled"}` : "off"} — set below
        </span>
      );
    case "studio_config_bool":
      return <span className="subtle">{c.enabled ? "on" : "off"} — set below</span>;
    default:
      return <span className="subtle">{c.reason}</span>;
  }
}

// ---------------------------------------------------------------------------
// Settings panels.
// ---------------------------------------------------------------------------

/**
 * The chat family's levers.
 *
 * Every write here is a READ-MODIFY-WRITE at SAVE time: a fresh
 * `getStudioConfig`, one pure transform, then the WHOLE config back.
 * `edit_studio_config` is full-replace and its wipe guard only reaches
 * top-level keys, so a hand-built partial patch would silently drop sub-keys
 * (`chat_translator.send_quick_ack`, every `studio.*` field) that nothing would
 * catch. Doing the read at save rather than at page load also closes the stale
 * window `/config` still has, where its in-memory config may be minutes old.
 */
function ChatSettings({
  slug,
  lanes,
  onSaved,
}: {
  slug: string | null;
  lanes: readonly AgentLaneRow[];
  onSaved: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async (
    mutate: (config: Record<string, unknown>) => Record<string, unknown>,
    section: string,
  ): Promise<void> => {
    if (slug === null) return;
    setBusy(true);
    setErr(null);
    try {
      const fresh = await api.getStudioConfig(slug);
      await api.saveStudioConfig(slug, {
        request_id: generateRequestIdV7(),
        patch: mutate(fresh.config as Record<string, unknown>),
        sections_touched: [section],
      });
      invalidateStudioConfig(slug);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const connectorLanes = lanes.filter((l) => l.control.kind === "connector");
  const webchat = lanes.find((l) => l.control.kind === "studio_config_bool");

  return (
    <div>
      <p className="subtle">
        The chat agent is one translator behind four surfaces. Each row below is
        that channel&apos;s own switch: off pauses the channel entirely, the other
        states decide how far the assistant may go without you.
      </p>
      {err !== null && <p className="empty">Could not save: {err}</p>}
      {connectorLanes.map((lane) => {
        const c = lane.control;
        /* c8 ignore next -- filtered above. */
        if (c.kind !== "connector") return null;
        const descriptor = CONNECTOR_DESCRIPTORS.find((d) => d.key === c.connector);
        if (descriptor?.ai === undefined) return null;
        return (
          <ConnectorAiRow
            key={lane.id}
            descriptor={descriptor}
            enabled={c.enabled}
            autonomy={(c.autonomy ?? descriptor.ai.defaultMode) as AiAutonomyMode}
            capability={
              (c.capability ?? descriptor.ai.defaultCapability) as ConnectorCapability
            }
            onChange={(next: ConnectorAiState) =>
              void save(
                (config) => applyConnectorState(config, c.connector as ConnectorKey, next),
                "connectors",
              )
            }
            onCapabilityChange={(next: ConnectorCapability) =>
              void save(
                (config) =>
                  applyConnectorCapability(config, c.connector as ConnectorKey, next),
                "connectors",
              )
            }
          />
        );
      })}

      {webchat !== undefined &&
        webchat.control.kind === "studio_config_bool" &&
        (() => {
          const c = webchat.control;
          const descriptor = CONNECTOR_DESCRIPTORS.find(
            (d) => d.chat !== undefined,
          );
          // No capability behind the flag (or no descriptor): the historical
          // bare checkbox. Kept as the honest fallback for a future flag-gated
          // lane rather than deleted — this branch is what a lane without a
          // connector still renders.
          if (c.capability === null || descriptor?.chat === undefined) {
            return (
              <div className="connector-row">
                <label>
                  <input
                    type="checkbox"
                    checked={c.enabled}
                    disabled={busy || slug === null}
                    onChange={(e) => {
                      // READ THE VALUE NOW, NOT INSIDE THE MUTATE CLOSURE.
                      // `save` invokes `mutate` AFTER `await
                      // api.getStudioConfig(slug)`, and by then React has
                      // re-rendered this controlled input back to its prop —
                      // the board data has not changed yet. Reading
                      // `e.target.checked` at that point returns the PRE-CLICK
                      // value, so the switch saved the state it was already in
                      // and answered 200: a control that could never change
                      // anything, and reported success every time. Caught on
                      // production 2026-08-11 by diffing the POST body, not by
                      // a test — `apps/dashboard/web/**` is outside both ESLint
                      // and coverage.
                      const next = e.target.checked;
                      void save(
                        (config) => applyWebchatEnabled(config, next),
                        "studio",
                      );
                    }}
                  />{" "}
                  <strong>Website chat widget</strong>
                </label>
              </div>
            );
          }
          // The three-state row. Its two writes land in different config
          // blocks, so "off" saves the studio flag alone and a capability
          // pick saves both — mirroring /config's `setConnectorChatState`.
          // The radio's value is captured by the closure argument, not read
          // off an event inside `mutate`, so the 2026-08-11 deferred-read
          // defect cannot recur here.
          return (
            <ConnectorChatRow
              descriptor={descriptor}
              enabled={c.enabled}
              capability={c.capability as ConnectorCapability}
              onChange={(next: ConnectorChatState) =>
                void save(
                  (config) =>
                    next === "off"
                      ? applyWebchatEnabled(config, false)
                      : applyWebchatEnabled(
                          applyConnectorCapability(config, "webchat", next),
                          true,
                        ),
                  "connectors",
                )
              }
            />
          );
        })()}

      <p className="subtle">
        Prompt, per-tier tool allow-lists and per-tier model live on{" "}
        <Link to="/config" className="data-member-link">
          /config
        </Link>
        .
      </p>
    </div>
  );
}

function PreloadSettings({
  cards,
  onSaved,
}: {
  cards: readonly PreloadAgentCard[];
  onSaved: () => void;
}): JSX.Element {
  if (cards.length === 0) {
    return <p className="empty">This family has no dashboard-editable preload.</p>;
  }
  return (
    <>
      {cards.map((card) => (
        <div key={card.agent}>
          {card.runner !== null && card.runner.state !== "ok" && (
            <p className="subtle">
              This agent&apos;s runner is not ticking (last cycle{" "}
              {when(card.runner.last_tick_at)}). Edits still SAVE — the override is
              stored and applied the first time it ticks again — but nothing is
              reading them today.
            </p>
          )}
          {card.files.map((f) => (
            <AgentPreloadEditor
              key={f.file}
              agent={card.agent}
              file={f}
              disabled={card.runner !== null && card.runner.state !== "ok"}
              onSaved={async () => {
                onSaved();
                return Promise.resolve();
              }}
            />
          ))}
          <p className="subtle" style={{ fontSize: "0.82em" }}>
            GUARDS.md is not editable here by design — the safety contract changes
            through git, with a diff and CI.
          </p>
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Family card.
// ---------------------------------------------------------------------------

function FamilyCard({
  family,
  controlsAvailable,
  slug,
  preload,
  onSettled,
}: {
  family: AgentFamilyRow;
  controlsAvailable: boolean;
  slug: string | null;
  preload: ResourceSnapshot<PreloadResponse>;
  onSettled: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const preloadCards = (preload.data?.agents ?? []).filter((c: PreloadAgentCard) =>
    family.lanes.some((l) => l.preload_agent === c.agent),
  );

  return (
    <section className="home-card">
      <button
        type="button"
        className="home-card-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="home-card-caret">{open ? "▼" : "▶"}</span>
        <span className="home-card-title">{family.label}</span>
        <span
          className={STATUS_TAG[family.status]}
          title={STATUS_HINT[family.status]}
        >
          {STATUS_LABEL[family.status]}
        </span>
        <span className="home-card-headline">{family.headline}</span>
      </button>

      {open && (
        <div className="home-card-body">
          <p className="subtle">{family.description}</p>

          <h3 className="section-title">Lanes</h3>
          <table className="agents-lane-table">
            <thead>
              <tr>
                <th>Lane</th>
                <th>Runs on</th>
                <th>Last beat</th>
                <th>Last activity</th>
                <th>Control</th>
              </tr>
            </thead>
            <tbody>
              {family.lanes.map((lane) => (
                <tr key={lane.id}>
                  <td data-label="Lane">
                    <div>
                      <span
                        className={STATUS_TAG[lane.status]}
                        title={STATUS_HINT[lane.status]}
                      >
                        {STATUS_LABEL[lane.status]}
                      </span>{" "}
                      {lane.label}
                    </div>
                    {/* The hint again, in the open — a `title` is invisible on
                        touch and to anyone not hunting for it, and "what would
                        make this run" is the whole reason these four states are
                        separate words. Only for the states that need an answer;
                        `idle` and `live` are self-explanatory one-liners. */}
                    {(lane.status === "stood_down" ||
                      lane.status === "not_yet_run" ||
                      lane.status === "unobserved") && (
                      <div className="agents-status-note">
                        {STATUS_HINT[lane.status]}
                      </div>
                    )}
                    {/* What it DOES. The label names it and "Runs on" says
                        where it lives; without this the table answered every
                        question about a lane except whether the reader should
                        care about it. */}
                    <div className="agents-lane-description">
                      {lane.description}
                    </div>
                    {lane.control_unread && (
                      <div className="agents-status-note">
                        Ran with its control flag UNREAD (fail-open) — the pause
                        switch is not reaching this lane.
                      </div>
                    )}
                    {/* Without this the row reads as a click that did nothing:
                        the button flips (it shows the DESIRED state) while the
                        pill stays paused (it shows the OBSERVED one). */}
                    {lane.resume_pending && (
                      <div className="agents-status-note">
                        Resumed — but its last beat still reports the pause, so it
                        is not running yet. It picks the change up on its next
                        scheduled run.
                      </div>
                    )}
                  </td>
                  <td data-label="Runs on">
                    <span className="subtle">{lane.runtime_home}</span>
                  </td>
                  <td data-label="Last beat">
                    {lane.heartbeat === null ? (
                      // An inferred `live` must never look like a monitored one:
                      // this lane has no probe, and the pill is standing on its
                      // last dispatch alone.
                      <span className="subtle">
                        {lane.observed_via === "activity"
                          ? "no probe — inferred from its last dispatch"
                          : "not observed"}
                      </span>
                    ) : (
                      <>
                        {when(lane.heartbeat.tested_at)}
                        {lane.heartbeat.detail !== null && (
                          <div className="subtle" style={{ fontSize: "0.8em" }}>
                            {lane.heartbeat.detail}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td data-label="Last activity">{when(lane.last_activity_at)}</td>
                  <td data-label="Control">
                    <ControlCell
                      lane={lane}
                      onSettled={onSettled}
                      controlsAvailable={controlsAvailable}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="section-title">Recent activity</h3>
          <ActivityPanel agent={family.id} open={open} />

          <h3 className="section-title">Settings</h3>
          {family.id === "chat" ? (
            <ChatSettings slug={slug} lanes={family.lanes} onSaved={onSettled} />
          ) : preloadCards.length > 0 ? (
            <PreloadSettings cards={preloadCards} onSaved={onSettled} />
          ) : (
            <p className="empty">
              This family&apos;s settings live in git. See{" "}
              {family.docs.map((d, i) => (
                <span key={d}>
                  {i > 0 ? ", " : ""}
                  <code>{d}</code>
                </span>
              ))}
              .
            </p>
          )}

          {family.board_href !== null && (
            <p className="home-card-more">
              <Link to={family.board_href}>Open its board →</Link>
            </p>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

export function Agents(): JSX.Element {
  const board = useCachedResource<AgentsBoardResponse>("agents", api.agentsBoard);
  const preload = useCachedResource<PreloadResponse>("preload", api.preload);
  const [slug, setSlug] = useState<string | null>(null);

  useEffect(() => {
    void api
      .whoami()
      .then((w) => setSlug(w.studio.slug))
      .catch(() => setSlug(null));
  }, []);

  // `useCachedResource` has no imperative refetch: `invalidate` force-stales
  // the entry and bumps its subscribers, and the hook's own effect re-arms the
  // background fetch off that bump. Calling a fetcher here directly would
  // bypass the store's in-flight dedupe.
  const reload = useCallback(() => {
    invalidateAgents();
  }, []);

  const data = board.data;
  if (data === undefined) {
    return board.error !== null ? (
      <p className="empty">Could not load agents: {board.error.message}</p>
    ) : (
      <p className="empty">Loading agents…</p>
    );
  }

  const s = data.summary;
  return (
    <div className="page-agents">
      <h1 className="page-title">Agents</h1>
      <p className="subtle">
        What runs, where it runs, and whether it is running. A lane shows a switch
        only where a runner actually reads one — everywhere else the card says what
        turns it off instead.
      </p>

      {!data.controls_available && (
        <div className="agents-status-note">
          The <code>agent_controls</code> table is not present on this database
          yet, so every pause switch is read-only. Apply migration 165.
        </div>
      )}

      <div className="stat-grid">
        <div className="stat-card">
          <div className="label">Families</div>
          <div className="value">{s.families}</div>
        </div>
        {/* The counts after this one are per-LANE and sum to `lanes`, not to
            `families`. Rendering families alone made the strip read as counts
            of one thing that visibly disagreed (8 families, 28 lanes' worth of
            statuses). The denominator is what makes them add up. */}
        <div className="stat-card">
          <div className="label">Lanes</div>
          <div className="value">{s.lanes}</div>
        </div>
        <div className="stat-card">
          <div className="label">Live</div>
          <div className="value">{s.live}</div>
        </div>
        <div className="stat-card">
          <div className="label">Paused</div>
          <div className="value">{s.paused}</div>
        </div>
        <div className="stat-card">
          <div className="label">Needs attention</div>
          <div className="value">{s.degraded}</div>
        </div>
        {/* One card per resting state rather than one "Dormant" card holding
            all four. The old card read 7 while meaning "4 switched off in July,
            2 monthly crons awaiting their first run, 1 resting" — a number no
            operator could act on, because the three parts take three different
            actions. */}
        <div className="stat-card" title={STATUS_HINT.stood_down}>
          <div className="label">Stood down</div>
          <div className="value">{s.stood_down}</div>
        </div>
        <div className="stat-card" title={STATUS_HINT.not_yet_run}>
          <div className="label">Not yet run</div>
          <div className="value">{s.not_yet_run}</div>
        </div>
        <div className="stat-card" title={STATUS_HINT.idle}>
          <div className="label">Idle</div>
          <div className="value">{s.idle}</div>
        </div>
        <div className="stat-card" title={STATUS_HINT.unobserved}>
          <div className="label">Unobserved</div>
          <div className="value">{s.unobserved}</div>
        </div>
      </div>

      {data.families.map((family) => (
        <FamilyCard
          key={family.id}
          family={family}
          controlsAvailable={data.controls_available}
          slug={slug}
          preload={preload}
          onSettled={reload}
        />
      ))}
    </div>
  );
}
